<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import {
  VideoCamera,
  Calendar,
  User,
  Mic,
  Mute,
  VideoCamera as VideoIcon,
  Phone,
  Plus,
  Edit,
  Delete,
  ChatLineRound
} from '@element-plus/icons-vue'
import { useChatStore } from '/src/stores/chat'
import { useAuthStore } from '/src/stores/auth'
import { ElMessage, ElMessageBox } from 'element-plus'

const chatStore = useChatStore()
const authStore = useAuthStore()

const meetingTabs = ref([
  { key: 'active', label: '进行中' },
  { key: 'scheduled', label: '预约' },
  { key: 'my', label: '我的会议' },
  { key: 'manage', label: '管理' }
])
const activeMeetingTab = ref('active')
const showCalendar = ref(false)
const isInMeeting = ref(false)
const showCreateDialog = ref(false)

const newRoom = ref({
  name: '',
  type: 'public',
  allowJoin: true,
  allowRecord: true,
  requirePassword: false,
  password: ''
})

const isAdmin = computed(() => {
  const team = authStore.currentTeam
  const user = authStore.user
  return team?.ownerId === user?.id || team?.adminIds?.includes(user?.id)
})

const activeRooms = computed(() =>
  chatStore.availableRooms.filter(r => r.status === 'active')
)

const scheduledMeetings = computed(() =>
  chatStore.availableRooms.filter(r => r.status === 'scheduled')
)

const myRooms = computed(() =>
  chatStore.availableRooms.filter(r => r.createdBy === authStore.user?.id)
)

const handleJoinMeeting = async (room) => {
  try {
    await chatStore.api?.joinMeeting(room.id)
    isInMeeting.value = true
    ElMessage.success('已加入会议')
  } catch (err) {
    ElMessage.error('加入会议失败')
  }
}

const handleLeaveMeeting = () => {
  isInMeeting.value = false
  ElMessage.success('已离开会议')
}

const handleCreateRoom = async () => {
  if (!newRoom.value.name.trim()) {
    ElMessage.warning('请输入房间名称')
    return
  }
  try {
    await chatStore.api?.createMeetingRoom(newRoom.value)
    ElMessage.success('创建成功')
    showCreateDialog.value = false
    newRoom.value = { name: '', type: 'public', allowJoin: true, allowRecord: true, requirePassword: false, password: '' }
    chatStore.fetchMeetingRooms()
  } catch (err) {
    ElMessage.error('创建失败')
  }
}

const handleDeleteRoom = async (room) => {
  try {
    await ElMessageBox.confirm(`确定要删除房间 "${room.name}" 吗?`, '提示', {
      type: 'warning'
    })
    await chatStore.api?.deleteMeetingRoom(room.id)
    ElMessage.success('删除成功')
    chatStore.fetchMeetingRooms()
  } catch (err) {
    if (err !== 'cancel') {
      ElMessage.error('删除失败')
    }
  }
}

const formatDate = (date) => {
  if (!date) return ''
  return new Date(date).toLocaleDateString('zh-CN', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

onMounted(() => {
  chatStore.fetchMeetingRooms()
})

watch(() => chatStore.activeTab, (tab) => {
  if (tab === 'meetings') {
    chatStore.fetchMeetingRooms()
  }
})
</script>

<template>
  <div class="meetings-view">
    <template v-if="!isInMeeting">
      <div class="meeting-tabs">
        <div
          v-for="tab in meetingTabs"
          :key="tab.key"
          class="meeting-tab"
          :class="{ 'is-active': activeMeetingTab === tab.key }"
          @click="activeMeetingTab = tab.key"
        >
          {{ tab.label }}
        </div>
        <div class="calendar-btn" @click="showCalendar = !showCalendar">
          <el-icon><Calendar /></el-icon>
        </div>
      </div>

      <div class="meeting-content">
        <template v-if="activeMeetingTab === 'active'">
          <div v-if="activeRooms.length > 0" class="rooms-list">
            <div
              v-for="room in activeRooms"
              :key="room.id"
              class="room-card"
            >
              <div class="room-status">
                <span class="status-dot active"></span>
                进行中
              </div>
              <div class="room-name">{{ room.name }}</div>
              <div class="room-info">
                <el-icon><User /></el-icon>
                <span>{{ room.participantCount || 0 }}人</span>
              </div>
              <div class="room-participants" v-if="room.participants?.length">
                <el-avatar
                  v-for="p in room.participants.slice(0, 3)"
                  :key="p.id"
                  :size="28"
                  :src="p.avatar"
                >
                  {{ p.nickname?.[0] }}
                </el-avatar>
                <span v-if="room.participants.length > 3" class="more">
                  +{{ room.participants.length - 3 }}
                </span>
              </div>
              <el-button type="primary" @click="handleJoinMeeting(room)">
                加入会议
              </el-button>
            </div>
          </div>
          <div v-else class="empty-state">
            <el-icon :size="48" color="#d1d5db"><VideoCamera /></el-icon>
            <p>暂无进行中的会议</p>
          </div>
        </template>

        <template v-else-if="activeMeetingTab === 'scheduled'">
          <div v-if="scheduledMeetings.length > 0" class="rooms-list">
            <div
              v-for="meeting in scheduledMeetings"
              :key="meeting.id"
              class="room-card scheduled"
            >
              <div class="room-status">
                <span class="status-dot scheduled"></span>
                预约中
              </div>
              <div class="room-name">{{ meeting.name }}</div>
              <div class="room-time">
                <el-icon><Calendar /></el-icon>
                {{ formatDate(meeting.scheduledTime) }}
              </div>
              <el-button type="primary" plain>
                等待开始
              </el-button>
            </div>
          </div>
          <div v-else class="empty-state">
            <el-icon :size="48" color="#d1d5db"><Calendar /></el-icon>
            <p>暂无预约会议</p>
          </div>
        </template>

        <template v-else-if="activeMeetingTab === 'my'">
          <div v-if="myRooms.length > 0" class="rooms-list">
            <div
              v-for="room in myRooms"
              :key="room.id"
              class="room-card"
            >
              <div class="room-type">
                <el-icon v-if="room.type === 'public'"><VideoCamera /></el-icon>
                <el-icon v-else><Lock /></el-icon>
                {{ room.type === 'public' ? '公开' : '私密' }}
              </div>
              <div class="room-name">{{ room.name }}</div>
              <div class="room-stats">
                参会: {{ room.joinCount || 0 }}次
              </div>
              <div class="room-actions">
                <el-button size="small" @click="handleJoinMeeting(room)">
                  进入
                </el-button>
              </div>
            </div>
          </div>
          <div v-else class="empty-state">
            <el-icon :size="48" color="#d1d5db"><VideoCamera /></el-icon>
            <p>暂无我的会议</p>
          </div>
        </template>

        <template v-else-if="activeMeetingTab === 'manage'">
          <template v-if="isAdmin">
            <div class="manage-header">
              <span>我的房间</span>
              <el-button type="primary" size="small" @click="showCreateDialog = true">
                <el-icon><Plus /></el-icon>
                创建房间
              </el-button>
            </div>
            <div v-if="myRooms.length > 0" class="rooms-list">
              <div
                v-for="room in myRooms"
                :key="room.id"
                class="room-card manage"
              >
                <div class="room-type">
                  {{ room.type === 'public' ? '公开' : '私密' }}
                </div>
                <div class="room-name">{{ room.name }}</div>
                <div class="room-stats">
                  参会: {{ room.joinCount || 0 }}次
                </div>
                <div class="room-actions">
                  <el-button size="small" text>
                    <el-icon><Edit /></el-icon>
                  </el-button>
                  <el-button size="small" text type="danger" @click="handleDeleteRoom(room)">
                    <el-icon><Delete /></el-icon>
                  </el-button>
                </div>
              </div>
            </div>
            <div v-else class="empty-state">
              <el-icon :size="48" color="#d1d5db"><VideoCamera /></el-icon>
              <p>暂无房间，点击上方创建</p>
            </div>
          </template>
          <div v-else class="empty-state">
            <el-icon :size="48" color="#d1d5db"><Lock /></el-icon>
            <p>只有管理员可以管理会议房间</p>
          </div>
        </template>
      </div>
    </template>

    <template v-else>
      <div class="in-meeting">
        <div class="meeting-video-area">
          <div class="video-placeholder">
            <el-icon :size="64" color="#9ca3af"><VideoCamera /></el-icon>
            <p>会议进行中</p>
          </div>
          <div class="participants-overlay">
            <el-avatar
              v-for="i in 3"
              :key="i"
              :size="48"
              class="participant-avatar"
            >
              用户{{ i }}
            </el-avatar>
          </div>
        </div>
        <div class="meeting-controls">
          <el-button :icon="Mic" circle />
          <el-button :icon="Mute" circle />
          <el-button :icon="VideoIcon" circle />
          <el-button :icon="Phone" type="danger" @click="handleLeaveMeeting">
            离开
          </el-button>
        </div>
        <div class="meeting-chat">
          <div class="chat-header">
            <el-icon><ChatLineRound /></el-icon>
            <span>文字聊天</span>
          </div>
          <div class="chat-messages">
            <p class="chat-empty">暂无消息</p>
          </div>
        </div>
      </div>
    </template>

    <el-dialog v-model="showCreateDialog" title="创建会议房间" width="360px">
      <el-form label-width="80px">
        <el-form-item label="房间名称">
          <el-input v-model="newRoom.name" placeholder="请输入房间名称" />
        </el-form-item>
        <el-form-item label="房间类型">
          <el-radio-group v-model="newRoom.type">
            <el-radio value="public">公开</el-radio>
            <el-radio value="private">私密</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="权限设置">
          <el-checkbox v-model="newRoom.allowJoin">允许自由加入</el-checkbox>
          <el-checkbox v-model="newRoom.allowRecord">允许录制</el-checkbox>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="showCreateDialog = false">取消</el-button>
        <el-button type="primary" @click="handleCreateRoom">创建</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<style scoped>
.meetings-view {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.meeting-tabs {
  display: flex;
  background: white;
  border-bottom: 1px solid #e5e7eb;
}

.meeting-tab {
  flex: 1;
  padding: 10px 8px;
  text-align: center;
  font-size: 13px;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s ease;
}

.meeting-tab:hover {
  color: #0ea5e9;
}

.meeting-tab.is-active {
  color: #0ea5e9;
  border-bottom: 2px solid #0ea5e9;
}

.calendar-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 12px;
  color: #6b7280;
  cursor: pointer;
}

.calendar-btn:hover {
  color: #0ea5e9;
}

.meeting-content {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
}

.rooms-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.room-card {
  background: white;
  border-radius: 10px;
  padding: 14px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.room-status {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #10b981;
  margin-bottom: 8px;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #10b981;
}

.status-dot.scheduled {
  background: #f59e0b;
}

.room-type {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 4px;
}

.room-name {
  font-size: 15px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 8px;
}

.room-info,
.room-time {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #6b7280;
  margin-bottom: 10px;
}

.room-stats {
  font-size: 12px;
  color: #9ca3af;
  margin-bottom: 10px;
}

.room-participants {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 12px;
}

.participant-avatar {
  margin-right: -8px;
  border: 2px solid white;
}

.more {
  font-size: 12px;
  color: #9ca3af;
  margin-left: 8px;
}

.room-actions {
  display: flex;
  gap: 8px;
}

.manage-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #9ca3af;
}

.empty-state p {
  margin: 12px 0;
}

.in-meeting {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.meeting-video-area {
  flex: 1;
  position: relative;
  background: #1f2937;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.video-placeholder {
  color: #9ca3af;
}

.participants-overlay {
  position: absolute;
  bottom: 12px;
  right: 12px;
  display: flex;
  gap: 8px;
}

.meeting-controls {
  display: flex;
  justify-content: center;
  gap: 12px;
  padding: 16px;
}

.meeting-chat {
  flex: 1;
  background: white;
  border-radius: 10px;
  margin-top: 12px;
  display: flex;
  flex-direction: column;
}

.chat-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-bottom: 1px solid #e5e7eb;
  font-size: 13px;
  font-weight: 500;
}

.chat-messages {
  flex: 1;
  padding: 12px;
  overflow-y: auto;
}

.chat-empty {
  text-align: center;
  color: #9ca3af;
  font-size: 13px;
}
</style>
